/**
 * TLF — Pincode Delivery Checker
 * Replaces Easify delivery-location dropdown.
 * Layer 1 of 2: UX / serviceability check only.
 * Actual shipping charge is handled natively by Shopify Local Delivery zones
 * (configured in Settings → Shipping and delivery). This script does NOT
 * calculate price — it only tells the customer if/where you deliver, and
 * what the charge will be, for trust + speed before they reach checkout.
 */

(function () {
  "use strict";

  // ---- 1. ZONE CONFIG ----------------------------------------------------
  // Edit this table only. Everything else is logic.
  // ranges: inclusive [from, to] on the 6-digit pincode as a number.
  const TLF_ZONES = [
    { city: "Delhi",            fee: 299, ranges: [[110001, 110096]] },
    { city: "Noida",            fee: 499, ranges: [[201301, 201309], [201313, 201313]] },
    { city: "Gurugram",         fee: 699, ranges: [[122001, 122018]] },
    { city: "Greater Noida",    fee: 699, ranges: [[201306, 201306], [201310, 201310], [201312, 201312]] },
    { city: "Faridabad",        fee: 899, ranges: [[121001, 121010]] },
    { city: "Ghaziabad",        fee: 899, ranges: [[201001, 201017]] }
  ];

  const STORAGE_KEY = "tlf_delivery_pincode";
  const STORAGE_TTL_MS = 1000 * 60 * 60 * 24 * 30; // 30 days

  // ---- 2. CORE LOOKUP ------------------------------------------------------
  function lookupPincode(pin) {
    const n = parseInt(pin, 10);
    for (const zone of TLF_ZONES) {
      for (const [from, to] of zone.ranges) {
        if (n >= from && n <= to) {
          return { serviceable: true, city: zone.city, fee: zone.fee };
        }
      }
    }
    return { serviceable: false };
  }

  function isValidPincode(pin) {
    return /^[1-9][0-9]{5}$/.test(pin);
  }

  // ---- 3. STORAGE ----------------------------------------------------------
  function saveResult(pin, result) {
    try {
      const payload = { pin, result, ts: Date.now() };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch (e) { /* storage unavailable — fail silent, non-blocking */ }
  }

  function loadSavedResult() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const payload = JSON.parse(raw);
      if (Date.now() - payload.ts > STORAGE_TTL_MS) return null;
      return payload;
    } catch (e) {
      return null;
    }
  }

  // ---- 4. RENDER -------------------------------------------------------------
  function renderResult(container, pin, result) {
    const resultBox = container.querySelector("[data-tlf-result]");
    const input = container.querySelector("[data-tlf-input]");
    if (input && pin) input.value = pin;

    if (result.serviceable) {
      resultBox.innerHTML =
        '<div class="tlf-pin-result tlf-pin-result--ok">' +
          '<span class="tlf-pin-result__icon">&#10003;</span>' +
          '<span>Delivering to <strong>' + escapeHtml(result.city) + '</strong> &middot; Delivery &#8377;' + result.fee + '</span>' +
          '<button type="button" class="tlf-pin-change" data-tlf-change>Change</button>' +
        '</div>';
      container.dispatchEvent(new CustomEvent("tlf:delivery-set", {
        bubbles: true,
        detail: { pincode: pin, city: result.city, fee: result.fee }
      }));
    } else {
      resultBox.innerHTML =
        '<div class="tlf-pin-result tlf-pin-result--no">' +
          '<span class="tlf-pin-result__icon">&#9888;</span>' +
          '<span>Sorry, we don’t deliver to this pincode yet.</span>' +
          '<button type="button" class="tlf-pin-change" data-tlf-change>Try another</button>' +
        '</div>' +
        '<form class="tlf-notify-form" data-tlf-notify-form>' +
          '<input type="email" placeholder="Email for launch updates" data-tlf-notify-email required>' +
          '<button type="submit">Notify me</button>' +
        '</form>';
    }
    container.classList.add("tlf-pin--resolved");
  }

  function escapeHtml(str) {
    const d = document.createElement("div");
    d.textContent = str;
    return d.innerHTML;
  }

  // ---- 5. WIRE UP EACH WIDGET ON PAGE -----------------------------------------
  function initWidget(container) {
    const form = container.querySelector("[data-tlf-form]");
    const input = container.querySelector("[data-tlf-input]");
    const resultBox = container.querySelector("[data-tlf-result]");

    function showServiceableCities() {
      const names = TLF_ZONES.map(z => z.city).join(", ");
      const hint = container.querySelector("[data-tlf-hint]");
      if (hint) hint.textContent = "We currently deliver to: " + names + ".";
    }
    showServiceableCities();

    // Pre-fill from a prior visit
    const saved = loadSavedResult();
    if (saved) {
      renderResult(container, saved.pin, saved.result);
    }

    form.addEventListener("submit", function (e) {
      e.preventDefault();
      const pin = input.value.trim();

      if (!isValidPincode(pin)) {
        resultBox.innerHTML = '<div class="tlf-pin-result tlf-pin-result--error">Please enter a valid 6-digit pincode.</div>';
        return;
      }

      const result = lookupPincode(pin);
      saveResult(pin, result);
      renderResult(container, pin, result);
    });

    container.addEventListener("click", function (e) {
      if (e.target.matches("[data-tlf-change]")) {
        container.classList.remove("tlf-pin--resolved");
        resultBox.innerHTML = "";
        input.value = "";
        input.focus();
        try { localStorage.removeItem(STORAGE_KEY); } catch (err) {}
      }
    });

    container.addEventListener("submit", function (e) {
      if (e.target.matches("[data-tlf-notify-form]")) {
        e.preventDefault();
        const email = e.target.querySelector("[data-tlf-notify-email]").value;
        const pin = input.value.trim();
        // Minimal, dependency-free capture: posts to Shopify's native
        // contact form endpoint so it lands in your store's inbox/notifications.
        // Swap this block out later for a Zoho/AiSensy webhook if preferred.
        fetch("/contact#ContactFooter", { method: "GET" }).catch(function () {});
        fetch(window.Shopify && window.Shopify.routes && window.Shopify.routes.root
          ? window.Shopify.routes.root + "contact"
          : "/contact", {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: new URLSearchParams({
            form_type: "contact",
            utf8: "✓",
            "contact[email]": email,
            "contact[body]": "Delivery waitlist request. Pincode: " + pin
          })
        }).then(function () {
          e.target.innerHTML = "<p>Thanks! We’ll email you when we launch here.</p>";
        }).catch(function () {
          e.target.innerHTML = "<p>Thanks! We’ve noted your interest.</p>";
        });
      }
    });
  }

  function init() {
    document.querySelectorAll("[data-tlf-pincode-widget]").forEach(initWidget);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  // Re-init if the product page re-renders a section (theme editor / variant swap)
  document.addEventListener("shopify:section:load", init);
})();